import React, { useEffect } from 'react';
import AudioWaveMark from '../shared/AudioWaveMark';

/**
 * Reusable confirm/danger dialog for destructive actions.
 *
 *   <ConfirmModal
 *     open={...}
 *     title="Delete Unmasked?"
 *     body="This can't be undone. Episodes stay on your account…"
 *     confirmLabel="Delete show"
 *     tone="danger"
 *     busy={isDeleting}
 *     onCancel={...}
 *     onConfirm={...}
 *   />
 */
export default function ConfirmModal({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel  = 'Cancel',
  busy = false,
  tone = 'danger',   // 'danger' | 'primary'
  onCancel,
  onConfirm,
}) {
  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === 'Escape' && !busy) onCancel?.();
      if (e.key === 'Enter'  && !busy) onConfirm?.();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel, onConfirm]);

  if (!open) return null;

  return (
    <div
      className="ws-modal-wrap ws-confirm-wrap"
      onClick={(e) => e.target === e.currentTarget && !busy && onCancel?.()}
    >
      <div className="ws-modal ws-confirm-modal" role="alertdialog" aria-modal="true">
        <div className="ws-confirm-head">
          <div className={`ws-confirm-icon ws-confirm-icon-${tone}`} aria-hidden="true">
            {tone === 'danger'
              ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9"  x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              )
              : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8"  x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              )}
          </div>
          <div className="ws-confirm-titles">
            <h2 className="ws-confirm-title">{title}</h2>
            {body && <p className="ws-confirm-body">{body}</p>}
          </div>
        </div>

        <div className="ws-confirm-actions">
          <button
            type="button"
            className="ws-btn-ghost"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`ws-btn-confirm ws-btn-confirm-${tone}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? (
              <span className="ws-btn-confirm-busy">
                <AudioWaveMark animated />
                <span>Deleting…</span>
              </span>
            ) : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
